import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import {
  FormControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import {
  concatMap,
  filter,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';

import { ADDITIONAL_ADDRESS_TYPES, COUNTRY_LIST, validateUniqueObjectNumber, VALIDATORS } from 'app/constants';
import { ContactPerson as AdditionalAddress, ContactType, Object as Objekt } from 'graphql/models/generated';
import { DropdownItem } from 'models/dropdown.model';
import { combineLatest } from 'rxjs';
import { BaseComponent } from 'shared/base/base.component';
import { ConfirmModalService } from 'shared/services/confirm-modal.service';
import { CoreDataService } from 'shared/services/core-data.service';
import { FormService } from 'shared/services/form.service';
import { PermissionsService } from 'shared/services/permissions.service';
import { StoreService } from 'shared/services/store.service';

@Component({
  selector: 'app-objekt-basic-info',
  templateUrl: './basic-info.component.html',
  styleUrls: ['./basic-info.component.scss']
})
export class ObjektBasicInfoComponent extends BaseComponent implements OnInit {
  @Input() canAutoSave = true;

  @Input() isModal = false;

  @Output() onUpdateObject = new EventEmitter<boolean | undefined>();

  objekt?: Objekt;

  form!: UntypedFormGroup;

  formReady = false;

  isDealer = false;

  dropdownData = {
    countries: COUNTRY_LIST,
    additionalAddressTypes: ADDITIONAL_ADDRESS_TYPES
  };

  canUpdateObjekt!: boolean;

  removedAdditionalAddressIndex: number | null = null;

  get addressForm(): UntypedFormGroup {
    return this.form.controls.address as UntypedFormGroup;
  }

  get additionalAddresses(): UntypedFormArray {
    return this.form.controls.additionalAddresses as UntypedFormArray;
  }

  constructor(
    protected fb: UntypedFormBuilder,
    private formService: FormService,
    protected coreDataService: CoreDataService,
    private storeService: StoreService,
    private confirmModalService: ConfirmModalService,
    private permissionsService: PermissionsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    this.canUpdateObjekt = this.permissionsService.canUpdateObjekt();
    this.storeService.isCurrentAccountDealer().subscribe((val: boolean) => {
      this.isDealer = val;
      this.setupForm();
    })
  }

  setupForm(): void {
    this.storeService.setFormReady(this.formReady);
    
    combineLatest([
      this.storeService.getCurrentObjekt(),
      this.storeService.getOfferObject()
    ]).pipe(take(1))
      .subscribe(([currentObj, offerObj]: [Objekt | null, Objekt]) => {
        if (currentObj?.id) {
          this.objekt = currentObj;
        } else if (offerObj) {
          if (this.isDealer && offerObj.createdByManufacturer) {
            this.objekt = offerObj;
          } else if (!this.isDealer && !offerObj.createdByManufacturer) {
            this.objekt = offerObj;
          }
        }

        const additionalAddressesToAdd = this.objekt?.additionalAddresses || [];
        const additionalAddresses = this.fb.array(
          additionalAddressesToAdd.map((addr) => this.createAdditionalAddress(addr))
        );
        let objectNumberVal;

        if (this.objekt?.id && this.objekt?.objectNumber && this.objekt.id !== this.objekt.objectNumber) {
          objectNumberVal = this.objekt.objectNumber
        }

        this.form = this.fb.group({
          objectNumber: new FormControl(
            objectNumberVal || '',
            {
              updateOn: 'change',
              validators: [],
              asyncValidators: !this.isDealer
                ? validateUniqueObjectNumber(this.coreDataService, this.objekt?.id)
                : [],
            }
          ),
          name: [this.objekt?.name || '', [Validators.required, Validators.maxLength(VALIDATORS.MAX_LENGTH.INPUT_LONG)]],
          transactionType: [this.objekt?.transactionType || ''],
          submission: [this.objekt?.submission || ''],
          tenderNr: [this.objekt?.tenderNr || '', [Validators.maxLength(VALIDATORS.MAX_LENGTH.INPUT_SHORT)]],
          tenderName: [this.objekt?.tenderName || '', [Validators.maxLength(VALIDATORS.MAX_LENGTH.INPUT)]],
          address: this.formService.createAddressGroup(this.objekt?.address || {}),
          additionalAddresses
        });

        setTimeout(() => {
          const objectNumberControl = this.form.get('objectNumber');

          if (objectNumberControl) {

            objectNumberControl.statusChanges.pipe(
              filter(status => status !== 'PENDING'),
              take(1),
            ).subscribe();

            objectNumberControl.markAsTouched();
            objectNumberControl.markAsDirty();
            objectNumberControl.updateValueAndValidity({ onlySelf: true, emitEvent: true });
          }
        });

        if (this.canUpdateObjekt) {
          this.autoSaveSetup(this.objekt?.id);
        } else {
          this.form.disable({ emitEvent: false });
        }

        this.formReady = true;
        this.storeService.setFormReady(this.formReady);
      });
  }

  private autoSaveSetup(objectId: string | undefined): void {
    if (!this.canAutoSave || !objectId) {
      return;
    }

    this.form.valueChanges
      .pipe(
        filter(() => this.form.valid),
        concatMap((values) => {
          return this.coreDataService.updateObject(objectId, values)
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe(
        (obj) => {
          this.onUpdateObject.emit(obj.createdByManufacturer);
          this.updateAdditionalAddressForms(obj);
        },
        (err) => {
          console.error(err);
        }
      );

    this.form.controls.objectNumber.statusChanges
      .pipe(
        filter((value) => value === 'VALID' && this.form.controls.objectNumber.value !== ''),
        concatMap(() => this.coreDataService.updateObject(objectId, this.form.value)),
        takeUntil(this.destroyed$)
      ).subscribe((obj) => {
        this.onUpdateObject.emit(obj.createdByManufacturer);
        this.updateAdditionalAddressForms(obj);
      });
  }

  createAdditionalAddress(addr: AdditionalAddress): UntypedFormGroup {
    return this.fb.group({
      id: [addr.id || ''],
      companyName: [addr.companyName || ''],
      salutation: [addr.salutation || null, [Validators.required]],
      firstName: [addr.firstName || '', [Validators.required, Validators.maxLength(VALIDATORS.MAX_LENGTH.INPUT_SHORT)]],
      lastName: [addr.lastName || '', [Validators.required, Validators.maxLength(VALIDATORS.MAX_LENGTH.INPUT_SHORT)]],
      address: this.formService.createAddressGroup(addr.address || {}),
      email: [addr.email || '', [Validators.pattern(VALIDATORS.PATTERN.MAIL)]],
      phone: [addr.phone || '', [Validators.pattern(VALIDATORS.PATTERN.PHONE)]],
      fax: [addr.fax || '', [Validators.pattern(VALIDATORS.PATTERN.FAX)]],
      website: [addr.website || '', [Validators.maxLength(VALIDATORS.MAX_LENGTH.INPUT)]],
      contactType: [addr.contactType || ''],
      deleteOnUpdate: [false]
    });
  }

  addAdditionalAddress(item: DropdownItem<ContactType>): void {
    const additionalAddress = this.createAdditionalAddress({ contactType: item.value } as AdditionalAddress);
    this.additionalAddresses.push(additionalAddress);
  }

  onRemoveAdditionalAddress(index: number): void {
    if (this.canAutoSave && this.objekt?.id) {
      this.confirmModalService
        .show('DELETE_ADDITIONAL_ADDRESS')
        .onConfirm()
        .pipe(tap(() => this.removeAdditionalAddress(index)))
        .subscribe({
          error: (error: any) => {
            console.error('deleteAdditionalAddress.error', error);
          }
        });
    } else {
      this.removeAdditionalAddress(index);
    }
  }

  removeAdditionalAddress(index: number): void {
    if (this.canAutoSave && this.objekt?.id) {
      this.removedAdditionalAddressIndex = index;
      const additionalAddressForm = this.additionalAddresses.controls[index] as UntypedFormGroup;
      additionalAddressForm.controls.deleteOnUpdate.setValue(true);
    } else {
      this.additionalAddresses.removeAt(index);
    }
  }

  updateAdditionalAddressForms(obj: Objekt): void {
    if (this.removedAdditionalAddressIndex !== null) {
      this.form.disable({ emitEvent: false });
      this.additionalAddresses.removeAt(this.removedAdditionalAddressIndex);
      this.form.enable({ emitEvent: false });
      this.removedAdditionalAddressIndex = null;
    } else if (obj && obj.additionalAddresses) {
      obj.additionalAddresses.forEach((additionalAddress, i) => {
        const additionalAddressForm = this.additionalAddresses.controls[i] as UntypedFormGroup;
        if (additionalAddressForm.controls.id.value === '') {
          additionalAddressForm.controls.id.setValue(additionalAddress.id, { emitEvent: false });
        }
      });
    }
  }
}
